import { computed, DestroyRef, Inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable, throwError } from 'rxjs';

import {
	AbstractAccountSettingsService,
	AbstractAuthService,
	AbstractPasswordRestrictionsService,
	AbstractProfileService,
	ACCOUNT_SETTINGS_SERVICE_INJECTOR,
	AUTH_SERVICE_INJECTOR,
	HttpResponseHelpers,
	PASSWORD_RESTRICTIONS_SERVICE_INJECTOR,
	PROFILE_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';
import { RPCErrorCode, PasswordRestrictionsLetterCase } from '@campaign-portal/namespace/common/enums';
import {
	InvalidCurrentPasswordError,
	PasswordBlacklistedError,
	PasswordPreviouslyUsedError,
	PasswordRestrictions,
	PasswordValidationResult,
	PrivacyAndSecurityApi,
	PrivacyAndSecurityState,
	Reset2FACodeResult,
	Verify2FACodeResult,
} from '@campaign-portal/contracts/account-settings/privacy-and-security';

@Injectable({ providedIn: 'root' })
export class AlarisPrivacyAndSecurityAdapter extends PrivacyAndSecurityApi {
	readonly state = computed<PrivacyAndSecurityState>(() => ({
		twoFAEnabled: this.profileSignal()?.isEnable2FA ?? false,
		twoFAAvailable: !this.profileSignal()?.details?.isOwner,
		passwordDaysUntilExpires: this.profileSignal()?.daysUntilExpires,
	}));

	private readonly profileSignal = signal(this.profile.user);

	constructor(
		@Inject(ACCOUNT_SETTINGS_SERVICE_INJECTOR) private readonly account: AbstractAccountSettingsService,
		@Inject(PASSWORD_RESTRICTIONS_SERVICE_INJECTOR)
		private readonly restrictions: AbstractPasswordRestrictionsService,
		@Inject(PROFILE_SERVICE_INJECTOR) private readonly profile: AbstractProfileService,
		@Inject(AUTH_SERVICE_INJECTOR) private readonly auth: AbstractAuthService,
		destroyRef: DestroyRef
	) {
		super();
		this.profile.user$.pipe(takeUntilDestroyed(destroyRef)).subscribe((u) => this.profileSignal.set(u));
	}

	loadPasswordRestrictions(): Observable<PasswordRestrictions> {
		return this.restrictions.read().pipe(
			map((resp) => {
				const r = resp.Data[0];
				return {
					minLength: r.minPasswordLength,
					minDigits: r.minDigits,
					minLetters: r.minLetters,
					minSpecialChars: r.minSpecialChars,
					requiredLetterCases: r.requiredLetterCases.map((c) =>
						c === PasswordRestrictionsLetterCase.UPPERCASE ? 'uppercase' : 'lowercase'
					),
					specialCharsAllowed: r.specialCharsAllowed,
				};
			})
		);
	}

	validatePassword(password: string): Observable<PasswordValidationResult> {
		return this.account.validatePassword(password).pipe(
			map((resp) => ({
				blackList: resp.Data[0].blackList,
				previouslyUsed: resp.Data[0].previouslyUsed,
			}))
		);
	}

	changePassword(currentPassword: string, newPassword: string): Observable<void> {
		return this.account.resetPassword({ currentPassword, newPassword }).pipe(
			map((resp) => {
				if (resp.Success) {
					return;
				}
				if (HttpResponseHelpers.hasRPCError(resp, RPCErrorCode.INVALID_CURRENT_PASSWORD)) {
					throw new InvalidCurrentPasswordError();
				}
				if (HttpResponseHelpers.hasRPCError(resp, RPCErrorCode.PASSWORD_PREVIOUSLY_USED)) {
					throw new PasswordPreviouslyUsedError();
				}
				throw new PasswordBlacklistedError();
			})
		);
	}

	generatePassword(): Observable<string> {
		const userId = this.profile.user.id;
		if (!userId) {
			return throwError(() => new Error('User id is not available'));
		}
		return this.account.generatePassword(userId).pipe(map((resp) => resp.Data.password));
	}

	verifyCurrentPassword(password: string): Observable<void> {
		return this.account.verifyCurrentPassword({ currentPassword: password }).pipe(
			map((resp) => {
				if (resp.Success) {
					return;
				}
				throw new InvalidCurrentPasswordError();
			})
		);
	}

	deleteAccount(): Observable<void> {
		const user = this.profile.user;
		if (!user) {
			return throwError(() => new Error('User is not available'));
		}
		return this.account.update({ ...user, isActive: false }).pipe(
			map((resp) => {
				if (resp.Success) {
					this.auth.logOut();
				}
			})
		);
	}

	reset2FACode(): Observable<Reset2FACodeResult> {
		const email = this.profile.user.email;
		return this.account.reset2FACode({ Email: email }).pipe(map((resp) => ({ key: resp.Data.key })));
	}

	verify2FACode(code: string, key: string): Observable<Verify2FACodeResult> {
		const email = this.profile.user.email;
		return this.account.verify2FACode(email, code, key).pipe(
			map((resp) => {
				if (resp.Success && !resp.Data) {
					return { success: true } as const;
				}
				const incorrect = resp.Data;
				return {
					success: false,
					attempt: incorrect?.attempt ?? 0,
					timeToNextAttempt: incorrect?.timeToNextAttempt ?? 0,
				} as const;
			})
		);
	}
}
